import { Box, Button, Chip, InputBase, Typography, type BoxProps, type TypographyProps } from '@mui/material';
import { styled } from '@mui/material/styles';

const FONT_FAMILY = 'Montserrat, sans-serif';

const COLORS = {
  textPrimary: '#161c27',
  textSecondary: '#5f5e60',
  background: '#ffffff',
  surface: '#f8f6f4',
  textMuted: '#4a4949',
  surfaceHover: '#ebe8e5',
  primary: '#0e77da',
  primaryHover: '#0d6bc4',
  primarySoft: '#ebf1ff',
  border: '#adaba9',
};

const RADIUS_FULL = '100vmax';

export const Section = styled(Box)<BoxProps>(({ theme }) => ({
  padding: '1.5rem',
  paddingBottom: '3rem',
  backgroundColor: COLORS.background,

  [theme.breakpoints.up('lg')]: {
    padding: '1.5rem 10rem',
  },
}));

export const Container = styled(Box)<BoxProps>(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
  marginTop: '3rem',
}));

export const Header = styled(Box)<BoxProps>(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  textAlign: 'center',
}));

export const Title = styled(Typography)<TypographyProps>(() => ({
  fontFamily: FONT_FAMILY,
  fontWeight: 700,
  fontSize: '1.25rem',
  lineHeight: '1.5rem',
  color: COLORS.textPrimary,
}));

export const Subtitle = styled(Typography)<TypographyProps>(() => ({
  fontFamily: FONT_FAMILY,
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  color: COLORS.textPrimary,
}));

export const SearchWrapper = styled(Box)<BoxProps>(() => ({
  position: 'relative',
  maxWidth: '37.5rem',
  width: '100%',
  margin: '0 auto',
}));

export const SearchIconSlot = styled(Box)<BoxProps>(() => ({
  position: 'absolute',
  left: '1rem',
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  alignItems: 'center',
  color: COLORS.textSecondary,
  zIndex: 1,
}));

export const ClearButton = styled(Button)(() => ({
  minWidth: 'auto',
  padding: 0,
  position: 'absolute',
  right: '1rem',
  top: '50%',
  transform: 'translateY(-50%)',
  color: COLORS.textSecondary,
  textTransform: 'none',
  '&:hover': { backgroundColor: 'transparent', color: COLORS.textPrimary },
}));

export const SearchInput = styled(InputBase)(() => ({
  width: '100%',
  height: '3rem',
  padding: '0 2.5rem 0 3rem',
  borderRadius: RADIUS_FULL,
  border: `1px solid ${COLORS.border}`,
  backgroundColor: COLORS.background,
  fontFamily: FONT_FAMILY,
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  color: COLORS.textPrimary,

  '& input::placeholder': {
    color: COLORS.textPrimary,
    opacity: .5,
    fontWeight: '500'
  },

  '&:focus': {
    borderColor: COLORS.primary,
    boxShadow: '0 0 0 0.125rem rgba(14, 119, 218, 0.2)',
  },
}));

export const Categories = styled(Box)<BoxProps>(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.5rem',
  justifyContent: 'center',
}));

export const CategoryChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>(({ selected }) => ({
  height: '2.25rem',
  borderRadius: RADIUS_FULL,
  padding: '0 0.75rem',
  fontFamily: FONT_FAMILY,
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  backgroundColor: selected ? COLORS.primary : COLORS.surface,
  color: selected ? COLORS.background : COLORS.textPrimary,
  boxShadow: selected ? '0 0.25rem 0.5rem rgba(0, 0, 0, 0.18)' : 'none',
  '&:hover': {
    backgroundColor: selected ? COLORS.primaryHover : COLORS.surfaceHover,
  },
}));

export const MetaRow = styled(Box)<BoxProps>(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.5rem',
  flexWrap: 'wrap',
}));

export const MetaText = styled(Typography)<TypographyProps>(() => ({
  fontFamily: FONT_FAMILY,
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  color: COLORS.textSecondary,
}));

export const MetaActions = styled(Box)<BoxProps>(() => ({
  display: 'flex',
  gap: '0.5rem',
  flexWrap: 'wrap',
}));

export const GhostButton = styled(Button)(() => ({
  borderRadius: RADIUS_FULL,
  padding: '0.5rem 0.75rem',
  textTransform: 'none',
  backgroundColor: COLORS.surface,
  color: COLORS.textPrimary,
  fontFamily: FONT_FAMILY,
  fontSize: '0.875rem',
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
  '&:hover': { backgroundColor: COLORS.surfaceHover },
}));

export const List = styled(Box)<BoxProps>(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
}));

export const Card = styled(Box)<BoxProps>(() => ({
  backgroundColor: COLORS.surface,
  borderRadius: '0.75rem',
  overflow: 'hidden',
}));

export const CardButton = styled(Button)(() => ({
  width: '100%',
  textTransform: 'none',
  justifyContent: 'flex-start',
  padding: '1rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: '0.75rem',
  color: COLORS.textPrimary,
  '&:hover': { backgroundColor: COLORS.surfaceHover },
}));

export const CardHeader = styled(Box)<BoxProps>(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '0.75rem',
}));

export const CardHeaderContent = styled(Box)<BoxProps>(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  flex: 1,
}));

export const Badge = styled(Box)<BoxProps>(() => ({
  display: 'inline-block',
  padding: '0.25rem 0.5rem',
  borderRadius: RADIUS_FULL,
  backgroundColor: COLORS.primarySoft,
  fontFamily: FONT_FAMILY,
  fontSize: '0.75rem',
  lineHeight: '1rem',
  color: COLORS.primary,
  marginBottom: '0.5rem',
}));

export const Question = styled(Typography)<TypographyProps>(() => ({
  fontFamily: FONT_FAMILY,
  fontSize: '1rem',
  fontWeight: 400,
  lineHeight: '1.5rem',
  color: COLORS.textPrimary,
  textAlign: 'left',
}));

export const Answer = styled(Typography)<TypographyProps>(() => ({
  fontFamily: FONT_FAMILY,
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: '1.25rem',
  color: COLORS.textSecondary,
  textAlign: 'left',
}));

export const EmptyState = styled(Box)<BoxProps>(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.75rem',
  padding: '3rem 1rem',
  textAlign: 'center',
}));

export const EmptyTitle = styled(Typography)<TypographyProps>(() => ({
  fontFamily: FONT_FAMILY,
  fontSize: '1rem',
  lineHeight: '1.5rem',
  color: COLORS.textPrimary,
}));

export const EmptySubtitle = styled(Typography)<TypographyProps>(() => ({
  fontFamily: FONT_FAMILY,
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  color: COLORS.textSecondary,
}));

export const Highlight = styled('mark')(() => ({
  backgroundColor: COLORS.primarySoft,
  color: COLORS.primary,
  borderRadius: '0.25rem',
  padding: '0 0.125rem',
}));

export const InlineLink = styled('a')(() => ({
  color: COLORS.primary,
  fontWeight: 500,
  textDecoration: 'none',
  wordBreak: 'break-all',

  '&:hover': {
    textDecoration: 'underline',
  },
}));
